<template>
  <div ref="chart" class="dash-geo-map"></div>
</template>

<script>
import * as echarts from "echarts";
import shibeiGeo from "@/assets/geo/shibei.json";
import qingdaoGeo from "@/assets/geo/qingdao.json";
import { getFeatureCenter } from "@/utils/geoBounds";
import { isSameJson } from "@/utils/stableData";

const MAPS = {
  shibei: { name: "shibei", json: shibeiGeo, label: "市北区" },
  qingdao: { name: "qingdao", json: qingdaoGeo, label: "青岛市" }
};

let registered = false;
function ensureMaps() {
  if (registered) return;
  echarts.registerMap(MAPS.shibei.name, MAPS.shibei.json);
  echarts.registerMap(MAPS.qingdao.name, MAPS.qingdao.json);
  registered = true;
}

export default {
  name: "DashGeoMap",
  props: {
    mapNodes: { type: Array, default: () => [] },
    commonFont: { type: Number, default: 14 },
    /** shibei | qingdao */
    mapMode: { type: String, default: "shibei" },
    highlightDistrict: { type: String, default: "市北区" }
  },
  data() {
    return { chart: null, nodesSig: null };
  },
  watch: {
    mapNodes: {
      deep: true,
      handler(val) {
        if (isSameJson(val, this.nodesSig)) return;
        this.nodesSig = val;
        this.render();
      }
    },
    mapMode() { this.render(); },
    commonFont() {
      this.$nextTick(() => {
        this.chart && this.chart.resize();
        this.render();
      });
    }
  },
  mounted() {
    ensureMaps();
    this.chart = echarts.init(this.$refs.chart);
    this.chart.on("click", this.onChartClick);
    this.nodesSig = this.mapNodes;
    this.render();
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    if (this.chart) {
      this.chart.off("click", this.onChartClick);
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    onResize() {
      this.chart && this.chart.resize();
    },
    onChartClick(params) {
      if (!params || (params.seriesType !== "scatter" && params.seriesType !== "effectScatter")) {
        return;
      }
      const d = params.data || {};
      this.$emit("node-click", {
        id: d.id,
        name: d.name || params.name,
        memberCount: d.memberCount || 0,
        activityCount: d.activityCount || 0,
        activityTypes: d.activityTypes || [],
        lng: Array.isArray(d.value) ? d.value[0] : d.lng,
        lat: Array.isArray(d.value) ? d.value[1] : d.lat
      });
    },
    nodeCoords(node) {
      return [Number(node.lng), Number(node.lat)];
    },
    scatterData() {
      return (this.mapNodes || []).map(n => {
        const [lng, lat] = this.nodeCoords(n);
        return {
          id: n.id,
          name: n.name,
          value: [lng, lat, n.memberCount || 0],
          memberCount: n.memberCount || 0,
          activityCount: n.activityCount || 0,
          activityTypes: n.activityTypes || []
        };
      });
    },
    buildOption() {
      const fs = this.commonFont;
      const mode = MAPS[this.mapMode] ? this.mapMode : "shibei";
      const cfg = MAPS[mode];
      const scatter = this.scatterData();

      const geo = {
        map: cfg.name,
        roam: false,
        silent: true,
        label: { show: false },
        emphasis: {
          label: { show: false },
          itemStyle: { areaColor: "rgba(0, 195, 255, 0.35)" }
        },
        itemStyle: {
          areaColor: "rgba(10, 61, 122, 0.82)",
          borderColor: "#00c3ff",
          borderWidth: 1.5,
          shadowColor: "rgba(0, 195, 255, 0.45)",
          shadowBlur: 12
        }
      };

      if (mode === "shibei") {
        const c = getFeatureCenter(shibeiGeo.features[0]);
        geo.center = c;
        geo.zoom = 1.08;
      } else {
        geo.center = [120.38, 36.07];
        geo.zoom = 1.05;
        geo.regions = [
          {
            name: this.highlightDistrict,
            itemStyle: {
              areaColor: "rgba(0, 195, 255, 0.28)",
              borderColor: "#7dffc8",
              borderWidth: 2
            }
          }
        ];
        geo.itemStyle.areaColor = "rgba(8, 40, 90, 0.55)";
      }

      return {
        backgroundColor: "transparent",
        tooltip: {
          trigger: "item",
          backgroundColor: "rgba(6, 30, 70, 0.92)",
          borderColor: "rgba(0, 195, 255, 0.55)",
          textStyle: { color: "#fff", fontSize: fs },
          formatter(p) {
            if (p.seriesType !== "scatter" && p.seriesType !== "effectScatter") {
              return p.name || "";
            }
            const d = p.data || {};
            return (
              `<b style="color:#00c3ff">${p.name || d.name || ""}</b><br/>` +
              `会员 ${d.memberCount || 0} · 活动 ${d.activityCount || 0}`
            );
          }
        },
        geo,
        series: [
          {
            type: "scatter",
            coordinateSystem: "geo",
            data: scatter,
            symbol: "circle",
            symbolSize: Math.max(10, fs * 0.9),
            itemStyle: {
              color: "#00e5ff",
              shadowBlur: 8,
              shadowColor: "rgba(0, 229, 255, 0.65)"
            },
            label: {
              show: true,
              position: "top",
              distance: 4,
              color: "#d7e6ff",
              fontSize: Math.max(10, fs - 1),
              formatter: "{b}"
            },
            zlevel: 2
          },
          {
            type: "effectScatter",
            coordinateSystem: "geo",
            data: scatter,
            symbolSize: Math.max(6, fs * 0.55),
            rippleEffect: { brushType: "stroke", scale: 2.2 },
            itemStyle: { color: "#00c3ff", opacity: 0.75 },
            zlevel: 1
          }
        ]
      };
    },
    render() {
      if (!this.chart) return;
      this.chart.setOption(this.buildOption(), true);
    }
  }
};
</script>

<style lang="less" scoped>
.dash-geo-map {
  width: 100%;
  height: 100%;
  min-height: 220 / @rem2;
  cursor: pointer;
}
</style>
